import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Drag-to-resize for a side panel.
 *
 * Replaces two near-identical hand-rolled implementations in Chat.js that
 * shared the same refs — safe only because the page enforces one open panel at
 * a time. Three things this does that those didn't:
 *
 *  - Pointer capture instead of window mousemove. The old version stopped
 *    receiving events the instant the cursor crossed into the mockup preview's
 *    iframe, so the drag stuck. Capture routes every move back to the handle.
 *  - Restores the *previous* body cursor/userSelect in a cleanup, so unmounting
 *    mid-drag can't leave `ew-resize` stuck on the whole app.
 *  - Keyboard resize and double-click-to-reset. The old handles were mouse-only.
 *
 * @param {number}  min         Smallest allowed width, px.
 * @param {number}  max         Largest allowed width, px.
 * @param {number}  initial     Width before the user has touched it.
 * @param {string} [storageKey] localStorage key; omit to not persist.
 * @param {boolean} [invert]    True when the panel is on the right, so dragging
 *                              left makes it wider. Default true.
 * @param {boolean} [enabled]   False (e.g. mobile) returns width: undefined and
 *                              inert handlers, so callers need no ternary.
 */
export function useResizablePanel({
    min,
    max,
    initial,
    storageKey,
    invert = true,
    enabled = true,
}) {
    const clamp = useCallback((n) => Math.max(min, Math.min(max, n)), [min, max]);

    const [width, setWidth] = useState(() => {
        if (!storageKey) return initial;
        try {
            const stored = Number(window.localStorage.getItem(storageKey));
            // Clamp on read too: min/max may have changed since it was written.
            return Number.isFinite(stored) && stored > 0 ? clamp(stored) : initial;
        } catch {
            return initial;
        }
    });

    const [isResizing, setIsResizing] = useState(false);
    const drag = useRef(null);

    const persist = useCallback((value) => {
        if (!storageKey) return;
        try {
            window.localStorage.setItem(storageKey, String(value));
        } catch {
            /* private mode / quota — the width just won't survive a reload */
        }
    }, [storageKey]);

    const reset = useCallback(() => {
        if (!enabled) return;
        setWidth(initial);
        persist(initial);
    }, [enabled, initial, persist]);

    const onPointerDown = useCallback((e) => {
        if (!enabled || e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture?.(e.pointerId);
        drag.current = { startX: e.clientX, startWidth: width };
        setIsResizing(true);
    }, [enabled, width]);

    const onPointerMove = useCallback((e) => {
        if (!drag.current) return;
        const delta = invert
            ? drag.current.startX - e.clientX
            : e.clientX - drag.current.startX;
        setWidth(clamp(drag.current.startWidth + delta));
    }, [clamp, invert]);

    const endDrag = useCallback((e) => {
        if (!drag.current) return;
        e?.currentTarget?.releasePointerCapture?.(e.pointerId);
        drag.current = null;
        setIsResizing(false);
        // Persist once, on release — writing per move would be ~60 writes/sec.
        setWidth((w) => { persist(w); return w; });
    }, [persist]);

    const onKeyDown = useCallback((e) => {
        if (!enabled) return;
        const step = e.shiftKey ? 64 : 16;
        let next;
        switch (e.key) {
            case 'ArrowLeft': next = width + (invert ? step : -step); break;
            case 'ArrowRight': next = width + (invert ? -step : step); break;
            case 'Home': next = invert ? max : min; break;
            case 'End': next = invert ? min : max; break;
            default: return;
        }
        e.preventDefault();
        const clamped = clamp(next);
        setWidth(clamped);
        persist(clamped);
    }, [clamp, enabled, invert, max, min, persist, width]);

    // Body-level drag affordances. Kept in an effect so the cleanup runs even
    // if the panel unmounts mid-drag.
    useEffect(() => {
        if (!isResizing) return undefined;
        const { cursor, userSelect } = document.body.style;
        document.body.style.cursor = 'ew-resize';
        document.body.style.userSelect = 'none';
        return () => {
            document.body.style.cursor = cursor;
            document.body.style.userSelect = userSelect;
        };
    }, [isResizing]);

    const handleProps = enabled
        ? {
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onDoubleClick: reset,
            onKeyDown,
            role: 'separator',
            'aria-orientation': 'vertical',
            'aria-valuenow': width,
            'aria-valuemin': min,
            'aria-valuemax': max,
            'aria-label': 'Resize panel',
            tabIndex: 0,
        }
        : {};

    return { width: enabled ? width : undefined, isResizing, handleProps, reset };
}

export default useResizablePanel;
